import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

/**
 * Previous / Next article navigation — fetched from
 * `/api/blog/prev-next/{slug}` which orders by publishedAt within the
 * same vertical.
 *
 * Renders nothing if the endpoint returns { previous: null, next: null }
 * or if the fetch fails (never blocks rendering of the article body).
 */
export default function PrevNextNav({ slug }) {
  const [nav, setNav] = useState({ previous: null, next: null });

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    fetch(`/api/blog/prev-next/${encodeURIComponent(slug)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled && data) setNav(data);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [slug]);

  if (!nav.previous && !nav.next) return null;

  return (
    <nav
      aria-label="Article navigation"
      className="my-14 border-t border-neutral-200 pt-8 grid grid-cols-1 sm:grid-cols-2 gap-4"
      data-testid="prev-next-nav"
    >
      {nav.previous ? (
        <Link
          to={`/blog/${nav.previous.slug}`}
          className="group block border border-neutral-200 rounded-lg p-4 hover:border-neutral-900 transition-colors"
          data-testid="prev-article"
          rel="prev"
        >
          <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">
            ← Previous
          </div>
          <div className="font-semibold text-neutral-900 line-clamp-2 group-hover:text-neutral-950">
            {nav.previous.title}
          </div>
        </Link>
      ) : (
        <div />
      )}
      {nav.next ? (
        <Link
          to={`/blog/${nav.next.slug}`}
          className="group block border border-neutral-200 rounded-lg p-4 hover:border-neutral-900 transition-colors sm:text-right"
          data-testid="next-article"
          rel="next"
        >
          <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">
            Next →
          </div>
          <div className="font-semibold text-neutral-900 line-clamp-2 group-hover:text-neutral-950">
            {nav.next.title}
          </div>
        </Link>
      ) : (
        <div />
      )}
    </nav>
  );
}
